import {
  PointerEvent as ReactPointerEvent,
  useEffect,
  useRef,
  useState
} from 'react'

import { moveItem } from '@/api/models/parking'

type ItemRect = {
  top: number
  height: number
}

type DragState<T> = {
  id: string
  index: number
  items: T[]
  startY: number
  rects: ItemRect[]
  gap: number
}

type Props<T> = {
  items: T[]
  getId: (item: T) => string
  onReorder: (items: T[]) => void
}

export const useSortableList = <T>({ items, getId, onReorder }: Props<T>) => {
  const [previewItems, setPreviewItems] = useState<T[] | null>(null)
  const [draggingId, setDraggingId] = useState<string | null>(null)
  const [draggingOffset, setDraggingOffset] = useState(0)

  const elements = useRef(new Map<string, HTMLElement>())
  const refCallbacks = useRef(
    new Map<string, (element: HTMLElement | null) => void>()
  )
  const dragState = useRef<DragState<T> | null>(null)
  const previewRef = useRef<T[] | null>(null)
  const latest = useRef({ items, getId, onReorder })

  latest.current = { items, getId, onReorder }

  useEffect(() => {
    if (dragState.current) {
      return
    }

    previewRef.current = null
    setPreviewItems(null)
  }, [items])

  const registerItem = (id: string) => {
    const cached = refCallbacks.current.get(id)

    if (cached) {
      return cached
    }

    const callback = (element: HTMLElement | null) => {
      if (element) {
        elements.current.set(id, element)
      } else {
        elements.current.delete(id)
      }
    }

    refCallbacks.current.set(id, callback)

    return callback
  }

  useEffect(() => {
    if (!draggingId) {
      return
    }

    const finishDrag = () => {
      dragState.current = null
      setDraggingId(null)
      setDraggingOffset(0)
    }

    const handleMove = (event: globalThis.PointerEvent) => {
      const state = dragState.current

      if (!state) {
        return
      }

      const { rects, index, startY, gap } = state
      const { getId: getItemId } = latest.current
      const draggedRect = rects[index]
      const offset = event.clientY - startY
      const draggedCenter = draggedRect.top + draggedRect.height / 2 + offset
      const centerOf = (position: number) =>
        rects[position].top + rects[position].height / 2

      let target = index

      while (
        target < rects.length - 1 &&
        draggedCenter > centerOf(target + 1)
      ) {
        target += 1
      }

      while (target > 0 && draggedCenter < centerOf(target - 1)) {
        target -= 1
      }

      const nextItems = moveItem(state.items, index, target)

      previewRef.current = nextItems
      setPreviewItems(nextItems)

      let nextTop = rects[0].top

      for (const item of nextItems) {
        if (getItemId(item) === state.id) {
          break
        }

        const itemIndex = state.items.findIndex(
          candidate => getItemId(candidate) === getItemId(item)
        )

        nextTop += rects[itemIndex].height + gap
      }

      setDraggingOffset(draggedRect.top + offset - nextTop)
    }

    const handleUp = () => {
      const state = dragState.current
      const nextItems = previewRef.current
      const { getId: getItemId, onReorder: reorderItems } = latest.current

      finishDrag()

      if (!state || !nextItems) {
        return
      }

      const isChanged = nextItems.some(
        (item, index) => getItemId(item) !== getItemId(state.items[index])
      )

      if (isChanged) {
        reorderItems(nextItems)

        return
      }

      previewRef.current = null
      setPreviewItems(null)
    }

    const handleCancel = () => {
      finishDrag()
      previewRef.current = null
      setPreviewItems(null)
    }

    window.addEventListener('pointermove', handleMove)
    window.addEventListener('pointerup', handleUp)
    window.addEventListener('pointercancel', handleCancel)

    return () => {
      window.removeEventListener('pointermove', handleMove)
      window.removeEventListener('pointerup', handleUp)
      window.removeEventListener('pointercancel', handleCancel)
    }
  }, [draggingId])

  const handlePointerDown = (id: string) => (event: ReactPointerEvent) => {
    if (event.button !== 0) {
      return
    }

    const index = items.findIndex(item => getId(item) === id)

    if (index === -1) {
      return
    }

    const rects: ItemRect[] = []

    for (const item of items) {
      const element = elements.current.get(getId(item))

      if (!element) {
        return
      }

      const { top, height } = element.getBoundingClientRect()

      rects.push({ top, height })
    }

    event.preventDefault()

    dragState.current = {
      id,
      index,
      items,
      startY: event.clientY,
      rects,
      gap:
        rects.length > 1 ? rects[1].top - (rects[0].top + rects[0].height) : 0
    }

    previewRef.current = items
    setPreviewItems(items)
    setDraggingId(id)
    setDraggingOffset(0)
  }

  return {
    sortableItems: previewItems ?? items,
    draggingId,
    draggingOffset,
    registerItem,
    getHandleProps: (id: string) => ({
      onPointerDown: handlePointerDown(id)
    })
  }
}
